import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  alpha,
  Tooltip,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  TableSortLabel,
  Button,
  IconButton,
} from '@mui/material';
import FlightIcon from '@mui/icons-material/Flight';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import type { TravelItem } from '../types/travel';
import { listTravels } from '../services/centralDbClient';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import NewTravelDialog from '../components/NewTravelDialog';

const formatTime = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const dtf = new Intl.DateTimeFormat(undefined, { month: 'short', day: '2-digit' });
  const tf = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });
  return `${dtf.format(d)} ${tf.format(d)}`;
};

const StatusChip: React.FC<{ status: TravelItem['status'] }> = ({ status }) => {
  const map: Record<TravelItem['status'], { label: string; color: 'default' | 'success' | 'warning' }> = {
    to_arrange: { label: 'To arrange', color: 'warning' },
    booked: { label: 'Booked', color: 'success' },
    past: { label: 'Past', color: 'default' },
  };
  const { label, color } = map[status];
  return <Chip size="small" label={label} color={color} variant={color === 'default' ? 'outlined' : 'filled'} />;
};

const InformedCell: React.FC<{ value: boolean; label: string }> = ({ value, label }) => (
  <Tooltip title={label}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {value ? (
        <CheckCircleIcon color="success" fontSize="small" />
      ) : (
        <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
      )}
    </Box>
  </Tooltip>
);

const TravelTypeIcon: React.FC<{ item: TravelItem }> = ({ item }) => {
  if (item.type === 'flight') {
    return <Tooltip title="Flight"><FlightIcon color="primary" /></Tooltip>;
  }
  if (item.type === 'car_optima') {
    return <Tooltip title="Optima car"><DirectionsCarIcon color="success" /></Tooltip>;
  }
  return <Tooltip title="Self-arranged car"><DirectionsCarIcon color="error" /></Tooltip>;
};

const FlightDetails: React.FC<{ item: TravelItem }> = ({ item }) => {
  if (item.type !== 'flight') return <span>—</span>;
  const route = item.route ? `${item.route.from} → ${item.route.to}` : '';
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.flightNumber || '—'}</Typography>
      <Typography variant="body2" color="text.secondary">{route}</Typography>
    </Stack>
  );
};

const PersonCell: React.FC<{ item: TravelItem }> = ({ item }) => {
  const ef = item.person?.easyflexRegistrationNumber ? `EF #${item.person.easyflexRegistrationNumber}` : '';
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <PersonOutlineIcon color="action" />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.person?.name || '—'}</Typography>
        {ef && (
          <Typography variant="caption" color="text.secondary">{ef}</Typography>
        )}
      </Box>
    </Stack>
  );
};

export default function TravelsPage() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { data: travelsRaw } = useQuery({
    queryKey: ['travels'],
    queryFn: () => listTravels(),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  const items: TravelItem[] = useMemo(() => {
    const arr = Array.isArray(travelsRaw) ? travelsRaw : [];
    return arr.map((t: any) => {
      const w = t.worker || {};
      const name = [w.firstName, w.lastName].filter(Boolean).join(' ') || '—';
      return {
        id: t.id,
        type: t.type,
        direction: t.direction,
        person: { id: w.id || '', name, email: w.email, easyflexRegistrationNumber: w.easyflexRegistrationNumber },
        route: t.routeFrom || t.routeTo ? { from: t.routeFrom || '', to: t.routeTo || '' } : undefined,
        flightNumber: t.flightNumber || undefined,
        departureAt: t.departureAt || undefined,
        arrivalAt: t.arrivalAt || undefined,
        status: 'to_arrange',
        driverName: t.type === 'flight' ? (t.driverName || '—') : undefined,
        informed: { person: false, client: false, driver: false },
      } as TravelItem;
    });
  }, [travelsRaw]);
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  // Filters (global + column-level)
  const [typeFilter, setTypeFilter] = useState<'all' | 'flight' | 'car_optima' | 'car_self'>('all');
  const [personFilter, setPersonFilter] = useState('');
  const [segmentFilter, setSegmentFilter] = useState<'all' | 'leaving' | 'coming'>('all');
  const [flightFilter, setFlightFilter] = useState('');
  const [leavingFilter, setLeavingFilter] = useState('');
  const [arrivalFilter, setArrivalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'to_arrange' | 'booked' | 'past'>('all');
  const [driverFilter, setDriverFilter] = useState('');
  const [informedPersonFilter, setInformedPersonFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [informedClientFilter, setInformedClientFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [informedDriverFilter, setInformedDriverFilter] = useState<'all' | 'yes' | 'no'>('all');

  // Sorting
  type Order = 'asc' | 'desc';
  type OrderBy = 'type' | 'person' | 'flight' | 'departureAt' | 'arrivalAt' | 'status' | 'driver' | 'infPerson' | 'infClient' | 'infDriver';
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('departureAt');

  const handleSort = (property: OrderBy) => () => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const applyFilters = (items: TravelItem[]) => items.filter((it) => {
    if (!it) return false;
    // Ensure required sub-objects exist (defensive for mixed data)
    const person = it.person || { name: '—', email: '', easyflexRegistrationNumber: undefined } as any;
    const informed = it.informed || { person: false, client: false, driver: false } as any;
    if (typeFilter !== 'all' && it.type !== typeFilter) return false;
    if (statusFilter !== 'all' && it.status !== statusFilter) return false;
    if (segmentFilter !== 'all') {
      if (!it.direction || it.direction !== segmentFilter) return false;
    }

    if (personFilter) {
      const s = personFilter.toLowerCase();
      const hay = `${person.name} ${person.email ?? ''} ${person.easyflexRegistrationNumber ?? ''}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }

    if (flightFilter) {
      const s = flightFilter.toLowerCase();
      if (it.type !== 'flight') return false;
      const routeStr = it.route ? `${it.route.from} ${it.route.to}`.toLowerCase() : '';
      const fn = (it.flightNumber || '').toLowerCase();
      if (!routeStr.includes(s) && !fn.includes(s)) return false;
    }

    if (driverFilter) {
      const s = driverFilter.toLowerCase();
      if (!(it.driverName || '').toLowerCase().includes(s)) return false;
    }

    if (leavingFilter) {
      const s = leavingFilter.toLowerCase();
      const val = formatTime(it.departureAt).toLowerCase();
      if (!val.includes(s)) return false;
    }

    if (arrivalFilter) {
      const s = arrivalFilter.toLowerCase();
      const val = formatTime(it.arrivalAt).toLowerCase();
      if (!val.includes(s)) return false;
    }

    const tri = (set: 'all' | 'yes' | 'no', v: boolean) => set === 'all' || (set === 'yes' ? v : !v);
    if (!tri(informedPersonFilter, informed.person)) return false;
    if (!tri(informedClientFilter, informed.client)) return false;
    if (!tri(informedDriverFilter, informed.driver)) return false;

    return true;
  });

  const comparator = (a: TravelItem, b: TravelItem) => {
    const cmp = (x: any, y: any) => (x < y ? -1 : x > y ? 1 : 0);
    const missLast = (xa: any, xb: any, compare: (x: any, y: any) => number) => {
      const aU = xa === undefined || xa === null || xa === '—';
      const bU = xb === undefined || xb === null || xb === '—';
      if (aU && bU) return 0;
      if (aU) return 1; // undefined last
      if (bU) return -1;
      return compare(xa, xb);
    };

    switch (orderBy) {
      case 'type': {
        const map: Record<TravelItem['type'], number> = { flight: 0, car_optima: 1, car_self: 2 };
        return cmp(map[a.type], map[b.type]);
      }
      case 'person':
        return cmp((a.person?.name || '—').toLowerCase(), (b.person?.name || '—').toLowerCase());
      case 'flight': {
        const as = `${a.flightNumber ?? ''} ${(a.route ? a.route.from + a.route.to : '')}`.toLowerCase();
        const bs = `${b.flightNumber ?? ''} ${(b.route ? b.route.from + b.route.to : '')}`.toLowerCase();
        return cmp(as, bs);
      }
      case 'departureAt': {
        const av = a.departureAt ? new Date(a.departureAt).getTime() : undefined;
        const bv = b.departureAt ? new Date(b.departureAt).getTime() : undefined;
        return missLast(av, bv, cmp);
      }
      case 'arrivalAt': {
        const av = a.arrivalAt ? new Date(a.arrivalAt).getTime() : undefined;
        const bv = b.arrivalAt ? new Date(b.arrivalAt).getTime() : undefined;
        return missLast(av, bv, cmp);
      }
      case 'status': {
        const sm: Record<TravelItem['status'], number> = { to_arrange: 0, booked: 1, past: 2 };
        return cmp(sm[a.status], sm[b.status]);
      }
      case 'driver':
        return cmp((a.driverName || '').toLowerCase(), (b.driverName || '').toLowerCase());
      case 'infPerson':
        return cmp(a.informed.person ? 1 : 0, b.informed.person ? 1 : 0);
      case 'infClient':
        return cmp(a.informed.client ? 1 : 0, b.informed.client ? 1 : 0);
      case 'infDriver':
        return cmp(a.informed.driver ? 1 : 0, b.informed.driver ? 1 : 0);
      default:
        return 0;
    }
  };

  const filteredSorted = useMemo(() => {
    const f = applyFilters(items);
    const s = [...f].sort((a, b) => (order === 'asc' ? comparator(a, b) : -comparator(a, b)));
    return s;
  }, [items, typeFilter, segmentFilter, personFilter, flightFilter, leavingFilter, arrivalFilter, statusFilter, driverFilter, informedPersonFilter, informedClientFilter, informedDriverFilter, order, orderBy]);

  const clearFilters = () => {
    setTypeFilter('all');
    setPersonFilter('');
    setSegmentFilter('all');
    setFlightFilter('');
    setLeavingFilter('');
    setArrivalFilter('');
    setStatusFilter('all');
    setDriverFilter('');
    setInformedPersonFilter('all');
    setInformedClientFilter('all');
    setInformedDriverFilter('all');
  };

  // removed effect-based fetch; data comes from React Query above

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Travels</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="contained" onClick={() => setAddOpen(true)}>Add Travel</Button>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as any)} displayEmpty>
              <MenuItem value="all">All types</MenuItem>
              <MenuItem value="flight">Flight</MenuItem>
              <MenuItem value="car_optima">Optima car</MenuItem>
              <MenuItem value="car_self">Self-arranged car</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} displayEmpty>
              <MenuItem value="all">All status</MenuItem>
              <MenuItem value="to_arrange">To arrange</MenuItem>
              <MenuItem value="booked">Booked</MenuItem>
              <MenuItem value="past">Past</MenuItem>
            </Select>
          </FormControl>
          <Button onClick={clearFilters} size="small" variant="outlined">Reset</Button>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        borderRadius: 1.5,
        overflow: 'auto',
        mx: 2,
      }}>
        <Table size="small" sx={{ minWidth: 1500, width: '100%' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.06) }}>
              <TableCell align="center" sx={{ width: 48 }}>
                <TableSortLabel active={orderBy==='type'} direction={orderBy==='type'?order:'asc'} onClick={handleSort('type')}>Type</TableSortLabel>
              </TableCell>
              <TableCell align="center">Segment</TableCell>
              <TableCell>
                <TableSortLabel active={orderBy==='person'} direction={orderBy==='person'?order:'asc'} onClick={handleSort('person')}>Person</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy==='flight'} direction={orderBy==='flight'?order:'asc'} onClick={handleSort('flight')}>Flight details</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy==='departureAt'} direction={orderBy==='departureAt'?order:'asc'} onClick={handleSort('departureAt')}>Leaving</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy==='arrivalAt'} direction={orderBy==='arrivalAt'?order:'asc'} onClick={handleSort('arrivalAt')}>Arrival</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy==='status'} direction={orderBy==='status'?order:'asc'} onClick={handleSort('status')}>Status</TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={orderBy==='driver'} direction={orderBy==='driver'?order:'asc'} onClick={handleSort('driver')}>Driver</TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel active={orderBy==='infPerson'} direction={orderBy==='infPerson'?order:'asc'} onClick={handleSort('infPerson')}>Person informed</TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel active={orderBy==='infClient'} direction={orderBy==='infClient'?order:'asc'} onClick={handleSort('infClient')}>Client informed</TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel active={orderBy==='infDriver'} direction={orderBy==='infDriver'?order:'asc'} onClick={handleSort('infDriver')}>Driver informed</TableSortLabel>
              </TableCell>
              <TableCell align="center">In credits/debits?</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
            {/* Column filters row */}
            <TableRow>
              <TableCell align="center">
                <FormControl size="small" fullWidth>
                  <Select value={typeFilter} onChange={(e)=>setTypeFilter(e.target.value as any)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="flight">Flight</MenuItem>
                    <MenuItem value="car_optima">Optima</MenuItem>
                    <MenuItem value="car_self">Self</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell align="center">
                <FormControl size="small" fullWidth>
                  <Select value={segmentFilter} onChange={(e)=>setSegmentFilter(e.target.value as any)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="leaving">Leaving</MenuItem>
                    <MenuItem value="coming">Coming</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <TextField size="small" fullWidth placeholder="Filter person" value={personFilter} onChange={(e)=>setPersonFilter(e.target.value)} />
              </TableCell>
              <TableCell>
                <TextField size="small" fullWidth placeholder="Flight no./route" value={flightFilter} onChange={(e)=>setFlightFilter(e.target.value)} />
              </TableCell>
              <TableCell>
                <TextField size="small" fullWidth placeholder="Leaving contains…" value={leavingFilter} onChange={(e)=>setLeavingFilter(e.target.value)} />
              </TableCell>
              <TableCell>
                <TextField size="small" fullWidth placeholder="Arrival contains…" value={arrivalFilter} onChange={(e)=>setArrivalFilter(e.target.value)} />
              </TableCell>
              <TableCell>
                <FormControl size="small" fullWidth>
                  <Select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value as any)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="to_arrange">To arrange</MenuItem>
                    <MenuItem value="booked">Booked</MenuItem>
                    <MenuItem value="past">Past</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <TextField size="small" fullWidth placeholder="Driver contains…" value={driverFilter} onChange={(e)=>setDriverFilter(e.target.value)} />
              </TableCell>
              <TableCell align="center">
                <FormControl size="small" fullWidth>
                  <Select value={informedPersonFilter} onChange={(e)=>setInformedPersonFilter(e.target.value as any)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell align="center">
                <FormControl size="small" fullWidth>
                  <Select value={informedClientFilter} onChange={(e)=>setInformedClientFilter(e.target.value as any)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell align="center">
                <FormControl size="small" fullWidth>
                  <Select value={informedDriverFilter} onChange={(e)=>setInformedDriverFilter(e.target.value as any)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell />
              <TableCell />
          </TableRow>
          </TableHead>
          <TableBody>
            {filteredSorted.map(item => (
              <TableRow key={item.id} hover>
                <TableCell align="center"><TravelTypeIcon item={item} /></TableCell>
                <TableCell align="center">
                  {item.direction ? (
                    <Chip size="small" label={item.direction === 'leaving' ? 'Leaving' : 'Coming'} color={item.direction === 'leaving' ? 'success' : 'default'} variant={item.direction === 'leaving' ? 'filled' : 'outlined'} />
                  ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell><PersonCell item={item} /></TableCell>
                <TableCell><FlightDetails item={item} /></TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatTime(item.departureAt)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatTime(item.arrivalAt)}</Typography>
                </TableCell>
                <TableCell><StatusChip status={item.status} /></TableCell>
                <TableCell>
                  {item.type === 'flight' ? (
                    <Typography variant="body2">{item.driverName || '—'}</Typography>
                  ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                  )}
                </TableCell>
                <TableCell align="center"><InformedCell value={item.informed.person} label="Person informed" /></TableCell>
                <TableCell align="center"><InformedCell value={item.informed.client} label="Client informed" /></TableCell>
                <TableCell align="center"><InformedCell value={item.informed.driver} label="Driver informed" /></TableCell>
                <TableCell align="center"><InformedCell value={!!item.inCreditsDebits} label="In credits/debits" /></TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditId(item.id)} aria-label="Edit travel">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
       </TableContainer>
      <NewTravelDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreate={(item) => {
          queryClient.invalidateQueries({ queryKey: ['travels'] });
          setAddOpen(false);
        }}
      />
      <NewTravelDialog
        open={!!editId}
        editId={editId || undefined}
        onClose={() => setEditId(null)}
        onUpdate={(item) => {
          queryClient.invalidateQueries({ queryKey: ['travels'] });
          setEditId(null);
        }}
      />
    </Container>
  );
}
